import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Flame } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { Reveal } from "./Reveal";

const pad = (n) => String(n).padStart(2, "0");

const Unit = ({ v, l, testid }) => (
  <div className="flex flex-col items-center">
    <motion.span key={v} initial={{ y: -8, opacity: 0.4 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.35 }}
      className="font-display text-2xl sm:text-3xl font-extrabold tabular-nums text-white" data-testid={testid}>{pad(v)}</motion.span>
    <span className="text-[10px] uppercase tracking-[0.2em] text-[#c9c6f0]">{l}</span>
  </div>
);

const Sep = () => <span className="font-display text-xl sm:text-2xl font-extrabold text-[#ffcf5c] pb-4">:</span>;

const Card = ({ m, now, i }) => {
  const { trial } = useSite();
  const left = Math.max(0, Math.floor(m.ts - now / 1000));
  const live = m.state === "in" || left === 0;
  const d = Math.floor(left / 86400), h = Math.floor((left % 86400) / 3600), mi = Math.floor((left % 3600) / 60), s = left % 60;
  const lang = m.league === "Süper Lig" ? "tr" : "en";

  return (
    <Reveal delay={i * 0.1} className="h-full">
      <div className="relative h-full overflow-hidden rounded-3xl border border-[#ffcf5c]/30 bg-[#13114f] p-6 md:p-7 flex flex-col gap-5" data-testid={`big-match-card-${m.id}`}>
        <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#ffcf5c]/15 blur-3xl" />
        <div className="relative flex items-center justify-between gap-3">
          <span className="derby-badge inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-extrabold tracking-wider text-[#2a1600]">
            <Flame size={12} /> {m.highlight} · {m.league}
          </span>
          <span className="text-xs text-[#c9c6f0] whitespace-nowrap">{m.day} · {m.time}</span>
        </div>
        <h3 lang={lang} className="relative font-display text-xl sm:text-2xl font-extrabold uppercase leading-tight text-[#ffe08a]" data-testid="big-match-teams">
          {m.home} <span className="text-sm font-semibold text-[#c9c6f0] normal-case px-1">{live && m.state === "in" ? `${m.home_score ?? 0} - ${m.away_score ?? 0}` : "vs"}</span> {m.away}
        </h3>
        <div className="relative mt-auto flex flex-wrap items-end justify-between gap-4">
          {live ? (
            <p className="inline-flex items-center gap-2 font-display text-xl font-extrabold uppercase" data-testid="big-match-live">
              <span className="h-3 w-3 rounded-full bg-red-500 animate-pulse" /> Şu an canlı
            </p>
          ) : (
            <div className="flex items-end gap-2 sm:gap-3" data-testid="big-match-timer">
              {d > 0 && <><Unit v={d} l="Gün" testid="countdown-days" /><Sep /></>}
              <Unit v={h} l="Saat" testid="countdown-hours" /><Sep />
              <Unit v={mi} l="Dakika" testid="countdown-minutes" /><Sep />
              <Unit v={s} l="Saniye" testid="countdown-seconds" />
            </div>
          )}
          <a href={trial} target="_blank" rel="noreferrer" data-testid={`big-match-trial-btn-${m.id}`}
            className="group inline-flex items-center gap-1.5 rounded-full bg-[#ffcf5c] px-5 py-3 text-sm font-semibold text-[#1a1033] hover:bg-white transition-colors shadow-[0_0_30px_rgba(255,207,92,0.3)]">
            {live ? "Hemen ücretsiz izle" : "Ücretsiz test al"}
            <ArrowUpRight size={16} className="transition-transform group-hover:rotate-45" />
          </a>
        </div>
      </div>
    </Reveal>
  );
};

export default function BigMatchCountdown({ matches }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const nowS = now / 1000;
  const open = matches.filter((m) => m.state !== "post" && nowS < m.ts + 3 * 3600).sort((a, b) => (a.state === "in" ? -1 : 0) - (b.state === "in" ? -1 : 0) || a.ts - b.ts);
  const derby = open.find((m) => m.highlight === "DERBİ");
  const other = open.find((m) => m.highlight !== "DERBİ");
  const picks = [derby, other].filter(Boolean);
  if (!picks.length) return null;

  return (
    <section className="relative bg-[#08072a] py-12 md:py-16" data-testid="big-match-countdown">
      <div className={`mx-auto max-w-7xl px-5 md:px-10 grid gap-4 ${picks.length > 1 ? "md:grid-cols-2" : ""}`}>
        {picks.map((m, i) => <Card key={m.id} m={m} now={now} i={i} />)}
      </div>
    </section>
  );
}
